import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { makeStyles } from '@material-ui/core';
import { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import { forwardRef } from 'react';
import { headerHeight, useDataTable } from '../../hooks/useDataTable';
import { pxToRem } from '../../theme';
import { NoResults } from './NoResults';
const useStyles = makeStyles((theme) => ({
    root: {
        overflow: 'visible',
        '& .ag-root-wrapper.ag-ltr': {
            borderLeft: 0,
            borderRight: 0,
            '& .ag-paging-button': {
                color: theme.palette.primary.main,
                '&.ag-disabled': {
                    color: theme.palette.secondary.main,
                },
            },
            '& .ag-column-drop': {
                background: '#fafafa',
                borderLeft: 0,
            },
            '& .ag-column-drop-cell-button .ag-icon': {
                color: '#1C94B9',
            },
            '& .ag-row': {
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                fontSize: pxToRem(12),
            },
            '& .ag-floating-filter-button-button, & .ag-header-icon, & .ag-sort-indicator-icon': {
                color: theme.palette.secondary.main,
            },
            '& .ag-header-cell[role="columnheader"]:hover': {
                background: '#d5dce299',
            },
            '& .ag-input-field-input': {
                minHeight: 24,
            },
            '& .ag-header-cell-text': {
                letterSpacing: '0.4px',
            },
            '& .ag-cell': {
                ' -webkit-line-clamp': 2,
                '-webkit-box-orient': 'vertical',
                display: '-webkit-box',
                height: 'auto',
                lineHeight: 1.2,
                overflow: 'hidden',
                overflowWrap: 'normal',
                whiteSpace: 'normal',
            },
            '& a': {
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                '&:hover, &:focus': {
                    textDecoration: 'none',
                },
            },
            '& .ag-cell-first-right-pinned': {
                alignItems: 'center',
                borderLeft: 0,
                display: 'flex',
                justifyContent: 'center',
                padding: 0,
            },
            '& .ag-pinned-left-header': {
                borderRight: 'unset',
                position: 'relative',
            },
            '& .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(.ag-cell-range-single-cell)': {
                borderRight: 'unset',
                padding: theme.spacing(0, 2),
            },
            '& .ag-paging-row-summary-panel, & .ag-paging-description': {
                color: theme.palette.common.black,
            },
        },
        '& .ag-column-drop-vertical': {
            minHeight: 90,
        },
        '& .ag-header-cell-resize': {
            alignSelf: 'anchor-center',
            height: 24,
            right: -5,
        },
        '& .ag-header-cell-resize::after': {
            height: '100%',
            top: 'unset',
            width: 1,
        },
        '& .ag-row-focus, & .ag-row-focus:hover': {
            backgroundColor: `${theme.palette.primary.light}`,
        },
        '& .ag-cell-single-line-row': {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
        },
        '& .ag-tool-panel-wrapper': {
            backgroundColor: '#f2f5f6',
            border: `1px solid ${theme.palette.divider}`,
            zIndex: 100,
            '& .ag-column-select-virtual-list-viewport': {
                maxHeight: 190,
                overflowY: 'scroll',
            },
        },
        '& .ag-column-select-header': {
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        '& .ag-checkbox-disabled .ag-checkbox-input': {
            pointerEvents: 'none',
        },
        '& .ag-cell-wrapper.ag-row-group': {
            alignItems: 'center',
        },
        '& [aria-label="Context Menu"]': {
            zIndex: 9999,
        },
        '& .ag-checkbox-input-wrapper.ag-checked::after': {
            color: theme.palette.primary.main,
        },
        '& .ag-column-select, & .ag-column-select-list': {
            overflow: 'unset',
        },
        '& .ag-column-panel': {
            display: 'block',
        },
        '& .ag-side-buttons': {
            display: 'none',
        },
        '& .ag-side-bar-right, & .ag-side-bar-left': {
            borderLeft: 0,
        },
        '& .ag-pinned-right-cols-container': {
            borderLeft: `1px solid ${theme.palette.divider}`,
        },
        '& .ag-filter-toolpanel-group-title-bar': {
            fontWeight: 400,
            fontSize: pxToRem(14),
            paddingLeft: theme.spacing(3),
            '& .ag-icon-tree-closed:before': {
                color: theme.palette.secondary.main,
            },
        },
    },
    fixedHeader: {
        position: 'relative',
        '& .ag-header.fixed-header': {
            position: 'fixed',
            top: headerHeight,
            left: 'auto',
            right: 'auto',
            width: 'auto',
            zIndex: 25,
            background: '#f2f5f6',
        },
        '& .ag-body-viewport.fixed-header': {
            marginTop: 80,
        },
    },
    fixedBottomScroll: {
        '& .ag-body-horizontal-scroll': {
            position: 'sticky',
            bottom: 0,
            zIndex: 25,
            background: '#fff',
        },
        '& .ag-body-horizontal-scroll.fixed-bottom': {
            position: 'fixed',
            bottom: 0,
            zIndex: 25,
        },
    },
}));
/**
 * DataTable component renders a table using `AgGridReact`.
 *
 * @param {DataTableProps} props - The properties for the DataTable component.
 * @returns {JSX.Element} The rendered DataTable component.
 */
export const DataTable = forwardRef(({ className, fixedBottomScroll = false, fixedHeader = false, height = '100%', width = '100%', ...props }, ref) => {
    const classes = useStyles();
    const { tableRef, handleGridReady, handleModelUpdated, getGridTheme, getCustomStyles } = useDataTable({
        fixedBottomScroll,
        fixedHeader,
        rowData: props.rowData,
        onGridReady: props.onGridReady,
        onModelUpdated: props.onModelUpdated,
    });
    return (_jsxs("div", { ref: tableRef, className: clsx(classes.root, { [classes.fixedHeader]: fixedHeader }, { [classes.fixedBottomScroll]: fixedBottomScroll }, className), "data-testid": 'data-grid-table', style: { height, width }, children: [_jsx("style", { children: getCustomStyles() }), _jsx(AgGridReact, { ref: ref, theme: getGridTheme(), noRowsOverlayComponent: NoResults, onGridReady: handleGridReady, onModelUpdated: handleModelUpdated, ...props })] }));
});
